/**
 * "Assessment basis" disclosure readout (issue #179): the active scenario's
 * turbine envelope with its provenance, the current eye height, and the
 * curvature/refraction coefficient the ANALYTICAL LOS layer applies — never
 * the rendered scene, which stays a flat local-grid projection
 * (core/coordinates.ts's bngToLocal). Lets a viewer confirm the geometry
 * matches the developer's own scoping figures and the standard method.
 *
 * Pure compute/format module (mirrors ui/coordinateReadout.ts's split);
 * main.ts refreshes it on scenario/viewpoint change only — never per frame
 * (repo performance rule). Never imports raypath/ (hard constraint #1):
 * CURVATURE_REFRACTION_K below is a viewer-side constant mirroring
 * raypath/curvature.py's DEFAULT_REFRACTION_K, cited in the comment so a
 * dedicated test (assessmentBasis.test.ts) can catch it drifting from the
 * real analytical value by reading that file's text — not by importing it.
 *
 * `text` is rendered as ONE element that WRAPS rather than a CSS ellipsis
 * (post-review fix): the original defect let a long free-text source
 * citation (one real scenario in turbine_scenarios_resolved.json runs past
 * 400 characters) push a CSS ellipsis to eat the curvature/flat-grid honesty
 * note that trailed it — the disclosure this feature exists to show never
 * reached the reader. `guaranteed` — the envelope (with any "not stated in
 * source" fallback) and the honesty note — always LEADS `text` and is kept
 * short by construction (bounded, no free-text scenario data), so it can
 * safely wrap without pushing #hud-bottom tall enough to collide with the
 * angular scale bar above it (issue #177, --bottom-stack-offset + 210px
 * clearance). The source citation, which genuinely has no length limit in
 * the data, is instead truncated at a fixed character count with `full`
 * preserving the untruncated text for a hover tooltip.
 */

/** Standard-atmosphere refraction coefficient applied by the analytical LOS
 * layer only (raypath/curvature.py, `DEFAULT_REFRACTION_K` = 0.13). Mirrored
 * here as a literal, never imported — this viewer has no dependency on
 * raypath/ (hard constraint #1). `tests/viewer/unit/assessmentBasis.test.ts`
 * guards this against drifting from the real value. */
export const CURVATURE_REFRACTION_K = 0.13;

/** Fixed cap on the rendered source citation (characters, excluding the
 * "…" suffix): bounds `text`'s length regardless of how long a scenario's
 * free-text source string is, so it can never wrap into an unbounded number
 * of lines. `full` always carries the untruncated citation. Long enough that
 * every real scenario's identifying reference (e.g. "ref EN0110031") in
 * data/config/turbine_scenarios_resolved.json survives — the point of a
 * citation is defeated if the truncation lands mid-identifier. */
const MAX_SOURCE_CHARS = 100;

export interface AssessmentBasisState {
  tipHeightM?: number;
  hubHeightM?: number;
  rotorDiameterM?: number;
  /** Free-text provenance for the active scenario's envelope, e.g. "MOD DIO
   * consultation response within PINS Scoping Opinion, ref EN0110031".
   * Absent -> disclosed as not stated, never fabricated (hard constraint #7).
   * Genuinely unbounded in length (real data runs past 400 characters), so
   * this is truncated in `text`, never in `guaranteed`. */
  source?: string;
  /** The eye height disclosed alongside this readout — the active
   * viewpoint's own `eye_height_m`, or the documented 1.6 m default. */
  eyeHeightM: number;
  /** True when `eyeHeightM` is the 1.6 m default rather than a viewpoint's
   * own stated value — never presented as though it were the viewpoint's own
   * figure (hard constraint #7). */
  eyeHeightIsDefault: boolean;
}

export interface AssessmentBasis {
  /** The full readout to render, in one normally-wrapping element. Always
   * starts with `guaranteed`; a long source citation is truncated here. */
  text: string;
  /** The turbine envelope (with any "not stated in source" fallback) and the
   * curvature/flat-grid honesty note — the disclosure this feature exists to
   * put in front of a reader, asserted independently so a regression that
   * drops or reorders it cannot hide behind `text` still "containing" the
   * words somewhere. */
  guaranteed: string;
  /** `text` with the source citation left completely untruncated — for a
   * hover tooltip, never displayed directly. */
  full: string;
}

function envelopeSegment(label: string, valueM: number | undefined): string {
  return valueM === undefined ? `${label} not stated in source` : `${valueM} m ${label}`;
}

function eyeHeightSegment(state: AssessmentBasisState): string {
  const value = `eye ${state.eyeHeightM.toFixed(1)} m`;
  return state.eyeHeightIsDefault ? `${value} (default)` : value;
}

function sourceSegment(source: string | undefined, truncate: boolean): string {
  if (!source) return '(source not stated)';
  if (!truncate || source.length <= MAX_SOURCE_CHARS) return `(${source})`;
  return `(${source.slice(0, MAX_SOURCE_CHARS)}…)`;
}

/**
 * Formats the assessment-basis readout. Missing envelope fields degrade to
 * an explicit "not stated in source" segment rather than a fabricated number
 * (hard constraint #7); a missing `source` string degrades the same way.
 * `guaranteed` always states that the render is a flat local grid: curvature
 * is an analytical-layer-only assumption, never applied to the 3D geometry
 * itself (issue #179's honesty requirement — the wording must not overstate
 * what the viewer's mesh does).
 */
export function formatAssessmentBasis(state: AssessmentBasisState): AssessmentBasis {
  const envelope = [
    envelopeSegment('tip', state.tipHeightM),
    envelopeSegment('hub', state.hubHeightM),
    envelopeSegment('rotor', state.rotorDiameterM),
  ].join(' / ');
  const guaranteed =
    `Turbines: ${envelope} · ` +
    `LOS curvature k=${CURVATURE_REFRACTION_K.toFixed(2)} (standard-atmosphere refraction, analytical layer only) · ` +
    `scene rendered on a flat local grid, not curvature-corrected`;
  const eye = eyeHeightSegment(state);
  return {
    guaranteed,
    text: `${guaranteed} · ${eye} · ${sourceSegment(state.source, true)}`,
    full: `${guaranteed} · ${eye} · ${sourceSegment(state.source, false)}`,
  };
}
